/**
 * Adapters turning each timetable source into the source-agnostic `InputJourney[]` that
 * `buildTimetableMatrix` consumes — so the bustimes-style grid renders identically for a
 * hosted/BODS network route and a user's own Fixed Route.
 *
 *  - Hosted networks: `RouteTrips` (real GTFS stop_times, seconds since midnight).
 *  - Own Fixed Routes: the synthetic `Timetable` produced by `generateTimetable`
 *    (HH:mm strings), split back into per-direction journeys.
 */

import type { RouteTrips } from '../../services/network/networkApi';
import type { Timetable, TimetableEntry } from './timetableUtils';
import type { FixedRoute } from '../../types/fixedRoutes';
import type { InputJourney } from './buildTimetableMatrix';

/** "HH:mm" → seconds since midnight (null on malformed input). */
export function hhmmToSeconds(time: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) return null;
  return Number(m[1]) * 3600 + Number(m[2]) * 60;
}

/**
 * Hosted/BODS route trips → journeys. Each trip is one column; its calls are ordered by
 * `stopSequence`. We surface the **departure** time (falling back to arrival), matching
 * how a public timetable reads "when the bus leaves this stop".
 */
export function routeTripsToJourneys(routeTrips: RouteTrips): InputJourney[] {
  return routeTrips.trips.map((trip) => {
    const calls = [...trip.stopTimes]
      .sort((a, b) => a.stopSequence - b.stopSequence)
      .map((st) => ({ stopId: st.stopId, seconds: st.departureS ?? st.arrivalS }));
    return {
      id: trip.tripId,
      headsign: trip.headsign ?? null,
      startSeconds: trip.startS ?? calls[0]?.seconds ?? null,
      stops: calls,
    };
  });
}

const sign = (n: number): number => (n > 0 ? 1 : n < 0 ? -1 : 0);

/**
 * Own Fixed Route synthetic timetable → journeys.
 *
 * `generateTimetable` emits a flat, arrival-sorted list of entries tagged with
 * `loopNumber` / `vehicleIndex`. We regroup by (vehicle, loop), then split each group at
 * direction changes (forward run vs. reverse-return run) using each stop's index in
 * `route.stops` — so an out-and-back route yields separate outbound/return columns rather
 * than one column that revisits stops.
 */
export function fixedRouteTimetableToJourneys(timetable: Timetable, route: FixedRoute): InputJourney[] {
  const seqIndex = new Map<string, number>();
  route.stops.forEach((s, i) => {
    if (!seqIndex.has(s.stopId)) seqIndex.set(s.stopId, i);
  });

  // Group by vehicle + loop, preserving generation/arrival order within each group.
  const groups = new Map<string, TimetableEntry[]>();
  timetable.timetableEntries.forEach((entry) => {
    const key = `${entry.vehicleIndex ?? 0}::${entry.loopNumber}`;
    const list = groups.get(key);
    if (list) list.push(entry);
    else groups.set(key, [entry]);
  });

  const journeys: InputJourney[] = [];

  groups.forEach((entries, key) => {
    // Split each group into maximal monotonic runs by stop index (forward vs. return).
    let run: TimetableEntry[] = [];
    let dir = 0;
    let runIdx = 0;

    const flush = () => {
      if (run.length === 0) return;
      const calls = run.map((e) => ({ stopId: e.stopId, seconds: hhmmToSeconds(e.departureTime) }));
      journeys.push({
        id: `${key}#${runIdx}`,
        headsign: run[run.length - 1].stopName,
        startSeconds: hhmmToSeconds(run[0].departureTime),
        stops: calls,
      });
      runIdx += 1;
      run = [];
      dir = 0;
    };

    entries.forEach((entry, i) => {
      if (i === 0) {
        run.push(entry);
        return;
      }
      const prev = seqIndex.get(entries[i - 1].stopId) ?? 0;
      const cur = seqIndex.get(entry.stopId) ?? 0;
      const d = sign(cur - prev);
      if (d !== 0 && dir !== 0 && d !== dir) {
        flush();
        run.push(entry);
      } else {
        run.push(entry);
        if (d !== 0) dir = d;
      }
    });
    flush();
  });

  return journeys;
}
